import { useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import EmailConfirmation from "../../../assets/svg/email-confirmation.svg";
import InputMask from "react-input-mask";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoginIcon from "@mui/icons-material/Login";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CheckIcon from "@mui/icons-material/Check";
import { PrimaryButton } from "../../../components/general/buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/general/buttons/SecondaryButton";
import { userApi } from "../../../services";
import { DocType, UserRole } from "../../../types/types";
import { registerSchema } from "../../../utils/yupSchemas";
import {
  CURRENT_SUBDOMAIN,
  DEMANDORIA_SUBDOMAINS,
  isCPFValid,
} from "../../../utils/stringUtils";
import { advanceStep, recedeStep } from "./functions";
import fetchCep from "../../../hooks/useCep";
import BackPageButton, {
  backButtonContainer,
} from "@/components/general/buttons/BackPageButton";
import { sharedStyles } from "@/styles/shared";
import { useTranslation } from "react-i18next";

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    t("register.steps.data"),
    t("register.steps.address"),
    t("register.steps.confirmation"),
  ];

  const [searchParams, setSearchParams] = useSearchParams();
  const roleSearchParam = searchParams.get("role");

  const [submittedOnce, setSubmittedOnce] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const birthDateRef = useRef<HTMLInputElement>(null);
  const phoneNumberRef = useRef<HTMLInputElement>(null);
  const [docNumber, setDocNumber] = useState("");
  const roleRef = useRef<HTMLSelectElement>(null);
  const docTypeRef = useRef<HTMLSelectElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmedPasswordRef = useRef<HTMLInputElement>(null);

  const personalDataRef = useRef({
    name: "",
    yearBirth: "",
    role: null,
    docType: null,
    docNumber: "",
    phoneNumber: "",
    email: "",
    password: "",
  });

  const streetRef = useRef<HTMLInputElement>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const districtRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef<HTMLInputElement>(null);

  const [cep, setCep] = useState<string>("");

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(registerSchema) });

  const handleUserCreation = async () => {
    try {
      await userApi.createUser({
        name: personalDataRef.current.name,
        email: personalDataRef.current.email,
        password: personalDataRef.current.password,
        docNumber: personalDataRef.current.docNumber,
        docType: personalDataRef.current.docType,
        role: personalDataRef.current.role,
        phone: personalDataRef.current.phoneNumber,
        logradouro: streetRef.current.value,
        number: numberRef.current.value,
        district: districtRef.current.value,
        city: cityRef.current.value,
        state: stateRef.current.value,
        country: "Brasil",
        CEP: cep,
        yearBirth: personalDataRef.current.yearBirth,
      } as Object);

      setActiveStep(2);
    } catch (error) {
      console.warn("Error creating user: ", error);
      setErrorMessage(error.response.data.message);
      setActiveStep(0);
    }
  };

  const foundedCepAddress = fetchCep(
    cep,
    streetRef,
    districtRef,
    cityRef,
    stateRef,
    true
  );

  const handleFirstStepSubmit = () => {
    if (!submittedOnce) setSubmittedOnce(true);

    if (isCPFValid(docNumber)) {
      personalDataRef.current = {
        name: nameRef.current.value,
        yearBirth: birthDateRef.current.value,
        role: roleRef.current.value,
        docType: docTypeRef.current.value,
        docNumber: docNumber,
        phoneNumber: phoneNumberRef.current.value,
        email: emailRef.current.value,
        password: passwordRef.current.value,
      };

      advanceStep(setActiveStep);
    }
  };

  const CpfField = (
    <InputMask
      mask="999.999.999-99"
      disabled={false}
      maskChar="_"
      value={docNumber}
      onChange={(e) => setDocNumber(e.target.value)}
    >
      <TextField
        label={t("register.form.cpf")}
        fullWidth
        autoComplete="off"
        defaultValue={personalDataRef.current?.docNumber ?? ""}
        error={submittedOnce && !isCPFValid(docNumber)}
        helperText={submittedOnce && !isCPFValid(docNumber) && t("register.errors.invalidCpf")}
      />
    </InputMask>
  );

  const firstStep = (
    <form
      onSubmit={onSubmit(handleFirstStepSubmit)}
      style={{ ...sharedStyles.form, gap: 20, width: "100%" }}
    >
      <TextField
        label={t("register.form.fullName")}
        type="text"
        inputRef={nameRef}
        fullWidth
        {...register("name")}
        error={!!errors.name}
        helperText={errors.name?.message}
      />

      <FormControl fullWidth>
        <InputLabel>{t("register.form.accountType")}</InputLabel>
        <Select
          label={t("register.form.accountType")}
          inputRef={roleRef}
          defaultValue={roleSearchParam ?? UserRole.COMERCIAL}
        >
          <MenuItem value={UserRole.CLIENT}>{t("register.options.client")}</MenuItem>
          <MenuItem value={UserRole.COMERCIAL}>{t("register.options.commercial")}</MenuItem>
        </Select>
      </FormControl>

      <Box sx={sharedStyles.horizontalContent}>
        <FormControl fullWidth sx={{ width: "50%" }}>
          <InputLabel>{t("register.form.docType")}</InputLabel>

          <Select
            label={t("register.form.docType")}
            inputRef={docTypeRef}
            defaultValue={DocType.CPF}
          >
            <MenuItem value={DocType.CPF}>{t("register.options.cpf")}</MenuItem>
            {/* <MenuItem value={DocType.PASSPORT}>Passaporte</MenuItem> */}
          </Select>
        </FormControl>

        {docTypeRef.current?.value === DocType.CPF ? CpfField : CpfField}
      </Box>

      <TextField
        label={t("register.form.birthDate")}
        type="date"
        inputRef={birthDateRef}
        {...register("yearBirth")}
        error={!!errors.yearBirth}
        helperText={errors.yearBirth?.message}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />

      <InputMask mask="(99) 9.9999-9999" disabled={false} maskChar="_">
        <TextField
          label={t("register.form.phone")}
          type="phone"
          fullWidth
          autoComplete="off"
          inputRef={phoneNumberRef}
          defaultValue={personalDataRef.current.phoneNumber ?? ""}
        />
      </InputMask>

      <TextField
        label={t("register.form.email")}
        type="email"
        inputRef={emailRef}
        placeholder={t("register.form.emailPlaceholder")}
        fullWidth
        {...register("email")}
        error={!!errors.email || errorMessage === "Email já cadastrado."}
      />

      <TextField
        label={t("register.form.password")}
        type="password"
        inputRef={passwordRef}
        fullWidth
        {...register("password")}
        error={!!errors.password}
        helperText={errors.password?.message}
      />

      <TextField
        label={t("register.form.confirmPassword")}
        type="password"
        inputRef={confirmedPasswordRef}
        fullWidth
        {...register("confirmedPassword")}
        error={!!errors.confirmedPassword}
        helperText={errors.confirmedPassword?.message}
      />

      {errorMessage !== null && <Alert severity="error">{errorMessage}</Alert>}

      <PrimaryButton
        label={t("register.actions.proceed")}
        icon={<CheckIcon />}
        styles={sharedStyles.secondaryColor}
      />
    </form>
  );

  const secondStep = (
    <form
      onSubmit={onSubmit(handleUserCreation)}
      style={{ ...sharedStyles.form, gap: 20, width: "100%" }}
    >
      <InputMask
        mask="99999-999"
        disabled={false}
        maskChar="_"
        value={cep}
        onChange={(e) => {
          setCep(e.target.value);
        }}
      >
        {<TextField label={t("register.form.zipCode")} fullWidth />}
      </InputMask>

      <Box sx={sharedStyles.horizontalContent}>
        <TextField
          label={t("register.form.street")}
          type="text"
          inputRef={streetRef}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={
            foundedCepAddress !== null && streetRef.current.value !== ""
          }
        />
        <TextField
          label={t("register.form.number")}
          type="text"
          inputRef={numberRef}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ width: "30%" }}
        />
      </Box>

      <TextField
        label={t("register.form.district")}
        type="text"
        inputRef={districtRef}
        InputLabelProps={{ shrink: true }}
        fullWidth
        disabled={
          foundedCepAddress !== null && districtRef.current.value !== ""
        }
      />

      <Box sx={sharedStyles.horizontalContent}>
        <TextField
          label={t("register.form.city")}
          type="text"
          inputRef={cityRef}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={foundedCepAddress !== null && cityRef.current.value !== ""}
        />

        <TextField
          label={t("register.form.state")}
          type="text"
          inputRef={stateRef}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={foundedCepAddress !== null && stateRef.current.value !== ""}
          sx={{ width: "30%" }}
        />
      </Box>

      <PrimaryButton
        label={t("register.actions.confirmAddress")}
        icon={<CheckIcon />}
        styles={sharedStyles.secondaryColor}
      />

      <SecondaryButton
        label={t("register.actions.back")}
        onClickAction={() => recedeStep(setActiveStep)}
      />
    </form>
  );

  const thirdStep = (
    <Box
      sx={{
        ...sharedStyles.form,
        alignItems: "center",
        gap: 5,
        width: "100%",
        maxWidth: "500px",
      }}
    >
      <Typography variant="h5" fontWeight="bold">
        {t("register.titles.welcome")}
      </Typography>

      <img
        src={EmailConfirmation}
        alt="email confirmation icon"
        width="120px"
        loading="lazy"
      />

      <Box
        sx={{
          ...sharedStyles.form,
          gap: 2,
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6">{t("register.titles.checkEmail")}</Typography>

        <Typography>
          {t("register.success.emailSent", { email: personalDataRef.current.email })}
        </Typography>
      </Box>

      <PrimaryButton
        label={t("register.actions.goToLogin")}
        icon={<LoginIcon />}
        onClickAction={() => navigate("/login")}
        styles={sharedStyles.secondaryColor}
      />
    </Box>
  );

  const renderContentByStep = () => {
    switch (activeStep) {
      case 0:
        return firstStep;
      case 1:
        return secondStep;
      default:
        return thirdStep;
    }
  };

  return (
    <>
      <Box sx={backButtonContainer}>
        <BackPageButton />
      </Box>

      <Box
        sx={{
          ...sharedStyles.form,
          alignItems: "center",
          marginX: { xs: 3, md: 3, lg: 10 },
          marginBottom: 5,
        }}
      >
        {CURRENT_SUBDOMAIN === DEMANDORIA_SUBDOMAINS[0] ? (
          <Typography variant="h4" fontWeight="bold">
            {t("register.titles.beauty")}
          </Typography>
        ) : (
          <Typography variant="h4" fontWeight="bold">
            {t("register.titles.pet")}
          </Typography>
        )}

        <Box
          sx={{
            ...sharedStyles.verticalContent,
            gap: 4,
            alignItems: "center",
            width: "100%",
            maxWidth: "500px",
          }}
        >
          <Stepper activeStep={activeStep} sx={{ width: "100%" }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>
                  <Typography
                    sx={{ display: { xs: "none", md: "block", lg: "block" } }}
                  >
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderContentByStep()}
        </Box>
      </Box>
    </>
  );
};

export default Register;